import { createSlice, createSelector } from "@reduxjs/toolkit";
import { coreApi } from "api/core";
import { setShowMessage } from "redux/slices/uiSlice";

const baseUrl = "/orders";
const DEFAULT_PAGE_LIMIT = 50;

const initialState = {
  pastDeliveries: [],
  timestamp: null,
  loading: false,
  pagination: {
    nextCursor: null,
    hasNextPage: false,
    isLoadingMore: false,
  },
};

const pastDeliveriesSlice = createSlice({
  name: "pastDeliveries",
  initialState,
  reducers: {
    setpastDeliveries: (state, action) => {
      state.pastDeliveries = action.payload;
      state.timestamp = +new Date();
    },
    appendPastDeliveries: (state, action) => {
      state.pastDeliveries = [...state.pastDeliveries, ...action.payload];
    },
    setpastDeliveriesLoading: (state) => {
      state.loading = true;
    },
    setsetpastDeliveriesReady: (state) => {
      state.loading = false;
    },
    setPastDeliveriesPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    resetPastDeliveriesPagination: (state) => {
      state.pagination = initialState.pagination;
    },
  },
});

export const {
  setpastDeliveries,
  appendPastDeliveries,
  setpastDeliveriesLoading,
  setsetpastDeliveriesReady,
  setPastDeliveriesPagination,
  resetPastDeliveriesPagination,
} = pastDeliveriesSlice.actions;

export default pastDeliveriesSlice.reducer;

function buildDeliveredUrl(from, to, tourIds, deliveryTimings, limit, cursor) {
  const path = `${baseUrl}/delivered/${from}/${to}/${encodeURIComponent(JSON.stringify(tourIds))}/${encodeURIComponent(JSON.stringify(deliveryTimings))}`;
  const params = new URLSearchParams();
  params.set("limit", String(limit ?? DEFAULT_PAGE_LIMIT));
  if (cursor) params.set("cursor", cursor);
  return `${path}?${params.toString()}`;
}

function buildDeliveryHistoryUrl(from, to, tourIds, deliveryTimings, limit, cursor) {
  const path = `${baseUrl}/delivery-history/${from}/${to}/${encodeURIComponent(JSON.stringify(tourIds))}/${encodeURIComponent(JSON.stringify(deliveryTimings))}`;
  const params = new URLSearchParams();
  params.set("limit", String(limit ?? DEFAULT_PAGE_LIMIT));
  if (cursor) params.set("cursor", cursor);
  return `${path}?${params.toString()}`;
}

export const getpastDeliveries =
  ({ from, to, tourIds, deliveryTimings, limit = DEFAULT_PAGE_LIMIT, cursor }) =>
  async (dispatch) => {
    const isLoadMore = !!cursor;
    if (isLoadMore) {
      dispatch(setPastDeliveriesPagination({ isLoadingMore: true }));
    } else {
      dispatch(setpastDeliveriesLoading());
      dispatch(resetPastDeliveriesPagination());
    }
    try {
      const url = buildDeliveredUrl(from, to, tourIds ?? [], deliveryTimings ?? [], limit, cursor);
      const response = await coreApi.axiosInstance.get(url);
      const data = Array.isArray(response.data) ? response.data : [];
      const nextCursor = response.headers?.["x-next-cursor"] ?? response.headers?.get?.("x-next-cursor") ?? null;

      if (isLoadMore) {
        dispatch(appendPastDeliveries(data));
      } else {
        dispatch(setpastDeliveries(data));
      }
      dispatch(
        setPastDeliveriesPagination({
          nextCursor,
          hasNextPage: !!nextCursor,
          isLoadingMore: false,
        })
      );
      return data;
    } catch (err) {
      console.error(err);
      dispatch(setPastDeliveriesPagination({ isLoadingMore: false }));
    } finally {
      if (!isLoadMore) dispatch(setsetpastDeliveriesReady());
    }
  };

export const getDeliveryHistory =
  ({ from, to, tourIds, deliveryTimings, limit = DEFAULT_PAGE_LIMIT, cursor }) =>
  async (dispatch) => {
    const isLoadMore = !!cursor;
    if (isLoadMore) {
      dispatch(setPastDeliveriesPagination({ isLoadingMore: true }));
    } else {
      dispatch(setpastDeliveriesLoading());
      dispatch(resetPastDeliveriesPagination());
    }
    try {
      const url = buildDeliveryHistoryUrl(from, to, tourIds ?? [], deliveryTimings ?? [], limit, cursor);
      const response = await coreApi.axiosInstance.get(url);
      const data = Array.isArray(response.data) ? response.data : [];
      const nextCursor = response.headers?.["x-next-cursor"] ?? response.headers?.get?.("x-next-cursor") ?? null;

      if (isLoadMore) {
        dispatch(appendPastDeliveries(data));
      } else {
        dispatch(setpastDeliveries(data));
      }
      dispatch(
        setPastDeliveriesPagination({
          nextCursor,
          hasNextPage: !!nextCursor,
          isLoadingMore: false,
        })
      );
      return data;
    } catch (err) {
      console.error(err);
      dispatch(setPastDeliveriesPagination({ isLoadingMore: false }));
    } finally {
      if (!isLoadMore) dispatch(setsetpastDeliveriesReady());
    }
  };

export const pastDeliveriesDocument = (from, to, tourIds, deliveryTimings, filters, t, path) => (dispatch) => {
  const url = `${import.meta.env.VITE_API}${baseUrl.replace(/\/\b/g, "")}/${path}/${from}/${to}/${JSON.stringify(
    tourIds
  )}/${JSON.stringify(deliveryTimings)}/${JSON.stringify(filters ?? {})}?taira=${localStorage.getItem("token")}`;
  dispatch(setpastDeliveriesLoading());
  // Since using coreApi.fetch directly doesn't deal with response so we have to download file through anchor tag
  try {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);

    dispatch(
      setShowMessage({
        description: t("File is downloading"),
        type: "success",
      })
    );
  } catch (err) {
    console.error(err);
    dispatch(
      setShowMessage({
        description: t("Download failed"),
        type: "error",
      })
    );
  } finally {
    dispatch(setsetpastDeliveriesReady());
  }
};

// const  pastDeliverySelector = ({  pastDeliveries }) =>  pastDeliveries.pastDelivery;
const pastDeliveriesSelector = ({ pastDeliveries }) => pastDeliveries.pastDeliveries;
const pastDeliveryStatusSelector = ({ pastDeliveries }) => pastDeliveries.loading;
const pastDeliveriesPaginationSelector = ({ pastDeliveries }) => pastDeliveries.pagination;

// export const selectTour = createSelector(tourSelector, (tour) => tour);
export const selectpastDeliveries = createSelector(pastDeliveriesSelector, (p) => p);
export const selectpastDeliveriesStatus = createSelector(pastDeliveryStatusSelector, (loading) => loading);
export const selectPastDeliveriesPagination = createSelector(
  pastDeliveriesPaginationSelector,
  (p) => p
);
